/**
 * Format price to currency string with thousands separator
 * @param priceInCents - Price in cents (e.g., 375 = $3.75)
 * @param currency - Currency symbol (default: '$')
 */
export function formatPrice(priceInCents: number, currency = '$'): string {
  const priceInDollars = priceInCents / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceInDollars);
  return `${currency}${formatted}`;
}

/**
 * Format date to time string (e.g., "8:14 PM")
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format date to short date string (e.g., "Jan 15")
 */
export function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format order number with padding (e.g., "#001", "#042", "#123")
 */
export function formatOrderNumber(orderNumber: number): string {
  return `#${orderNumber.toString().padStart(3, '0')}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Format number as percentage with thousands separator (e.g., "1,000%", "12.5%")
 * @param value - The percentage value (e.g., 12.5)
 * @param decimals - Number of decimal places (default: 0 for integers)
 */
export function formatPercent(value: number, decimals = 0): string {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  return `${formatted}%`;
}

/**
 * Convert 24h time string to 12h format with AM/PM
 * @param time24 - Time in "HH:MM" format (24h)
 * @returns Time in "H:MM AM/PM" format
 *
 * @example
 * formatTime12h("08:30") // "8:30 AM"
 * formatTime12h("14:00") // "2:00 PM"
 * formatTime12h("00:00") // "12:00 AM"
 */
export function formatTime12h(time24: string): string {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}
